import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/data/categories';

function CategoryDropdown() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState('');

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const href = e.target.value;
    if (!href) return;
    setSelected(href);
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mx-auto mt-6 flex max-w-xl flex-col gap-2">
      <label htmlFor="category-select" className="text-left text-sm font-medium text-foreground-600">
        {t('home.categories.dropdownLabel')}
      </label>
      <div className="relative">
        <select
          id="category-select"
          value={selected}
          onChange={handleChange}
          className="w-full appearance-none rounded-lg border border-background-300/70 bg-background-50 px-4 py-3 pr-10 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
        >
          <option value="">{t('home.categories.dropdownPlaceholder')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.href}>
              {t(`cat.${c.id}`)}
            </option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500" />
      </div>
    </div>
  );
}

function CategoryCard({ id, href, image }: { id: string; href: string; image: string }) {
  const { t } = useTranslation();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener nofollow"
      className="group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-44 w-full overflow-hidden">
        <img
          src={image}
          alt={`${t(`cat.${id}`)} collection`}
          title={`${t(`cat.${id}`)} collection`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex items-center justify-between gap-2 p-4">
        <span className="text-sm font-semibold text-foreground-900">{t(`cat.${id}`)}</span>
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
          <i className="ri-arrow-right-up-line text-sm" />
        </span>
      </div>
    </a>
  );
}

export default function CategorySection() {
  const { t } = useTranslation();

  return (
    <section id="categories" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{t('home.categories.title')}</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {t('home.categories.subtitle')}
        </p>
      </div>

      <CategoryDropdown />

      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {categories.map((c) => (
          <CategoryCard key={c.id} id={c.id} href={c.href} image={c.image} />
        ))}
      </div>
    </section>
  );
}